import { Icon, type IconName } from '@/design-system';
import type { GuideCategory } from '@/lib/api/v1';

const GUIDE_VISUALS: Record<
  GuideCategory,
  { label: string; icon: IconName; background: string; motif: string }
> = {
  syrup: {
    label: 'Sirup',
    icon: 'sparkles',
    background: 'linear-gradient(135deg, #6b2a1d 0%, #b86b2f 46%, #e3b764 100%)',
    motif: 'M18 64 C54 14 108 14 144 64 S234 114 270 64',
  },
  garnish: {
    label: 'Pynt',
    icon: 'cherry',
    background: 'linear-gradient(135deg, #234b38 0%, #677f35 48%, #d76a42 100%)',
    motif: 'M24 42 C72 76 116 76 164 42 S252 8 300 42',
  },
  ice: {
    label: 'Is',
    icon: 'snow',
    background: 'linear-gradient(135deg, #173343 0%, #3f7590 50%, #d6ecf2 100%)',
    motif: 'M20 88 L84 24 L148 88 L212 24 L276 88',
  },
  technique: {
    label: 'Teknikk',
    icon: 'chef-hat',
    background: 'linear-gradient(135deg, #2c2520 0%, #72533b 48%, #c99852 100%)',
    motif: 'M24 76 L96 28 L168 76 L240 28 L312 76',
  },
  prep: {
    label: 'Forberedelser',
    icon: 'container',
    background: 'linear-gradient(135deg, #323f33 0%, #786f3f 52%, #c9a44d 100%)',
    motif: 'M28 32 H112 V112 H28 Z M144 32 H228 V112 H144 Z',
  },
  'sober-curious': {
    label: 'Lavalkohol',
    icon: 'heart',
    background: 'linear-gradient(135deg, #33505d 0%, #6f897d 50%, #d8d0a6 100%)',
    motif: 'M32 84 C68 28 112 28 148 84 S228 140 264 84',
  },
};

export function GuideCover({
  category,
  title,
  variant = 'card',
}: {
  category: GuideCategory;
  title?: string;
  variant?: 'card' | 'hero';
}) {
  const visual = GUIDE_VISUALS[category];
  const isHero = variant === 'hero';

  return (
    <div
      className={`relative overflow-hidden rounded-md border border-line shadow-cellar ${
        isHero ? 'min-h-72 sm:min-h-80' : 'h-48'
      }`}
      style={{ background: visual.background }}
    >
      <svg
        aria-hidden
        viewBox="0 0 320 160"
        className="absolute inset-0 h-full w-full opacity-30"
        preserveAspectRatio="none"
      >
        <path d={visual.motif} fill="none" stroke="rgba(255,255,255,0.65)" strokeWidth="10" strokeLinecap="round" />
        <path d="M0 132 H320" fill="none" stroke="rgba(0,0,0,0.22)" strokeWidth="28" />
        <path d="M0 24 H320" fill="none" stroke="rgba(255,255,255,0.18)" strokeWidth="18" />
      </svg>
      <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0.06)_0%,rgba(0,0,0,0.34)_100%)]" />

      <div className="relative z-10 grid h-full content-between p-4 sm:p-5">
        <div className="flex items-center justify-between gap-3">
          <span className="inline-flex items-center gap-2 rounded-sm bg-black/45 px-2.5 py-1 text-xs font-semibold uppercase tracking-wider text-white backdrop-blur-sm">
            <Icon name={visual.icon} size={14} />
            {visual.label}
          </span>
        </div>

        {title ? (
          <div className="max-w-xl">
            <h2 className={`font-serif leading-tight text-white ${isHero ? 'text-4xl sm:text-5xl' : 'text-2xl'}`}>
              {title}
            </h2>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function guideCategoryLabel(category: GuideCategory): string {
  return GUIDE_VISUALS[category].label;
}

export function guideTimeLabel(minutes: number, long = false): string {
  if (minutes < 60) return long ? `${minutes} minutter` : `${minutes} min`;
  if (minutes < 1440) {
    const hours = Math.round(minutes / 60);
    return long ? `${hours} ${hours === 1 ? 'time' : 'timer'}` : `${hours} t`;
  }
  const days = Math.round(minutes / 1440);
  return long ? `${days} ${days === 1 ? 'dag' : 'dager'}` : `${days} dg`;
}

export function guideDifficultyLabel(difficulty: number): string {
  if (difficulty <= 1) return 'Lett';
  if (difficulty === 2) return 'Middels';
  return 'Avansert';
}
